import { useMutation } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import { Card, SwitchInput, useToast } from 'corso-admin-ui';
import { type CommandAdminApi } from 'corso-types';
import api from '~/api';
import {
  DataTable,
  DataTableStatus,
  sharedTableDefinitions,
} from '~/components/dataTable';
import {
  useRefetchStore,
  useStoreContext,
  useStoreId,
} from '~/providers/StoreProvider';

type FulfillmentLocation = CommandAdminApi.ResponseBody<
  '/store/:storeId',
  'get'
>['fulfillmentLocations'][number];

const { idColumn } = sharedTableDefinitions;

function useUpdateFulfillmentLocation() {
  const storeId = useStoreId();
  const refetch = useRefetchStore();
  const toast = useToast();
  return useMutation({
    mutationFn: ({
      locationId,
      body,
    }: {
      locationId: number;
      body: CommandAdminApi.RequestBody<
        '/store/:storeId/location/:locationId',
        'put'
      >;
    }) => api.store(storeId).updateLocation(locationId, body),
    onSuccess: () => {
      refetch();
      toast.show('Updated location');
    },
    onError: () => toast.show('Failed to update location'),
  });
}

function ReorderToggle({ location }: { location: FulfillmentLocation }) {
  const { mutate, isPending } = useUpdateFulfillmentLocation();
  return (
    <SwitchInput
      id={`location-reorder-${location.id}`}
      label="Allow reorder"
      labelVisuallyHidden
      checked={location.allowReorder}
      disabled={isPending}
      onChange={(event) =>
        mutate({
          locationId: location.id,
          body: { allowReorder: event.target.checked },
        })
      }
    />
  );
}

const locationColumns: ColumnDef<FulfillmentLocation>[] = [
  idColumn<FulfillmentLocation>({ meta: { align: 'left' } }),
  {
    accessorKey: 'name',
    header: 'Name',
    enableSorting: true,
    cell: ({ row }) => row.original.name ?? '—',
  },
  {
    accessorKey: 'allowReorder',
    header: 'Allow Reorder',
    meta: {
      align: 'right',
    },
    cell: ({ row }) => <ReorderToggle location={row.original} />,
  },
];

export default function LocationsCard() {
  const { fulfillmentLocations } = useStoreContext();

  return (
    <Card>
      <Card.Heading>Reorder Fulfillment Locations</Card.Heading>
      {fulfillmentLocations.length > 0 ? (
        <DataTable
          data={fulfillmentLocations}
          columns={locationColumns}
          filterable
          pageSize={10}
        />
      ) : (
        <DataTableStatus
          icon="location"
          title="No fulfillment locations"
          details="Fulfillment locations will appear here once they sync from the store."
        />
      )}
    </Card>
  );
}
